<template>
  <div class="devolucao-detalhes">
    <nav class="page-breadcrumb mb-1">
      <router-link :to="{ name: 'devolucoes' }" class="breadcrumb-link">
        <feather-icon icon="RotateCcwIcon" size="14" />
        <span>Devoluções e Trocas</span>
      </router-link>
      <feather-icon icon="ChevronRightIcon" size="14" class="breadcrumb-separator mx-25" />
      <span class="breadcrumb-current">Solicitação #{{ $route.params.id }}</span>
    </nav>

    <div v-if="isLoading" class="text-center py-5">
      <b-spinner variant="primary" label="Carregando..." />
    </div>

    <template v-else-if="devolucaoAtual">
      <b-row>
        <!-- Main info -->
        <b-col lg="8">
          <!-- Header card -->
          <b-card class="mb-1">
            <div class="d-flex align-items-start justify-content-between flex-wrap" style="gap:8px">
              <div>
                <h4 class="mb-25">
                  <b-badge :variant="tipoVariant" class="mr-50">
                    {{ devolucaoAtual.tipo === 'TROCA' ? 'Troca' : 'Devolução' }}
                  </b-badge>
                  Solicitação #{{ devolucaoAtual.devolucaoid }}
                </h4>
                <div class="text-muted small">
                  Criada em {{ formatDate(devolucaoAtual.created_at) }}
                  <span v-if="devolucaoAtual.usuario_nome"> por {{ devolucaoAtual.usuario_nome }}</span>
                </div>
              </div>
              <b-badge :variant="statusVariant(devolucaoAtual.status)" class="h5 px-1 py-50">
                {{ statusLabel(devolucaoAtual.status) }}
              </b-badge>
            </div>

            <hr />

            <b-row>
              <b-col md="4">
                <div class="text-muted small">Cliente</div>
                <div class="font-weight-bold">{{ devolucaoAtual.cliente_nome || devolucaoAtual.clienteid }}</div>
              </b-col>
              <b-col md="4" v-if="devolucaoAtual.pedidoid_origem">
                <div class="text-muted small">Pedido Origem</div>
                <div class="font-weight-bold">#{{ devolucaoAtual.pedidoid_origem }}</div>
              </b-col>
              <b-col md="4" v-if="devolucaoAtual.notafiscal">
                <div class="text-muted small">Nota Fiscal</div>
                <div class="font-weight-bold">{{ devolucaoAtual.notafiscal }}</div>
              </b-col>
            </b-row>

            <div class="mt-1">
              <div class="text-muted small">Motivo</div>
              <div>{{ devolucaoAtual.motivo }}</div>
            </div>
            <div v-if="devolucaoAtual.observacao" class="mt-50">
              <div class="text-muted small">Observação</div>
              <div>{{ devolucaoAtual.observacao }}</div>
            </div>
          </b-card>

          <!-- Items -->
          <b-card class="mb-1" v-if="devolucaoAtual.itens && devolucaoAtual.itens.length">
            <h6 class="mb-1">
              <feather-icon icon="PackageIcon" size="14" class="mr-25" />
              Itens da Solicitação ({{ devolucaoAtual.itens.length }})
            </h6>
            <b-table :items="devolucaoAtual.itens" :fields="itemFields" small responsive class="mb-0">
              <template #cell(descricao)="{ item }">
                <div>{{ item.descricao }}</div>
                <small class="text-muted">{{ item.referencia }}</small>
              </template>
              <template #cell(cor_tam)="{ item }">
                <small>{{ item.des_cor }}<span v-if="item.des_tamanho"> / {{ item.des_tamanho }}</span></small>
              </template>
            </b-table>
          </b-card>
        </b-col>

        <!-- Timeline + actions -->
        <b-col lg="4">
          <!-- Action card -->
          <b-card class="mb-1" v-if="canUpdateStatus">
            <h6 class="mb-1">
              <feather-icon icon="RefreshCwIcon" size="14" class="mr-25" />
              Atualizar Status
            </h6>
            <b-form-select v-model="novoStatus" size="sm" class="mb-75">
              <b-form-select-option :value="null" disabled>Selecione o novo status...</b-form-select-option>
              <b-form-select-option v-for="s in proximosStatus" :key="s.value" :value="s.value">
                {{ s.label }}
              </b-form-select-option>
            </b-form-select>
            <b-form-textarea v-model="obsStatus" rows="2" size="sm" placeholder="Observação (opcional)" class="mb-75" />
            <b-button
              size="sm" variant="primary" block :disabled="!novoStatus || isSaving"
              @click="atualizarStatus"
            >
              <b-spinner v-if="isSaving" small class="mr-25" />
              Confirmar
            </b-button>
          </b-card>

          <!-- Timeline -->
          <b-card>
            <h6 class="mb-1">
              <feather-icon icon="ClockIcon" size="14" class="mr-25" />
              Histórico de Status
            </h6>
            <div class="timeline">
              <div
                v-for="(h, idx) in historicoCrescente"
                :key="h.id"
                class="timeline-item"
                :class="{ 'timeline-item--last': idx === historicoCrescente.length - 1 }"
              >
                <div class="timeline-dot" :style="{ background: statusColor(h.status) }" />
                <div class="timeline-content">
                  <div class="d-flex align-items-center justify-content-between">
                    <b-badge :variant="statusVariant(h.status)" style="font-size:0.75rem">
                      {{ statusLabel(h.status) }}
                    </b-badge>
                    <small class="text-muted">{{ formatDatetime(h.created_at) }}</small>
                  </div>
                  <div v-if="h.observacao" class="mt-25 small">{{ h.observacao }}</div>
                  <div v-if="h.usuario_nome" class="mt-25 text-muted" style="font-size:0.72rem">
                    {{ h.usuario_nome }}
                  </div>
                </div>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </template>

    <b-alert v-else show variant="warning">Solicitação não encontrada.</b-alert>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

const STATUS_MAP = {
  AGUARDANDO:      { label: 'Aguardando',     variant: 'warning',   color: '#ff9f43' },
  EM_ANALISE:      { label: 'Em Análise',     variant: 'info',      color: '#00cfe8' },
  APROVADO:        { label: 'Aprovado',       variant: 'primary',   color: '#7367f0' },
  REPROVADO:       { label: 'Reprovado',      variant: 'danger',    color: '#ea5455' },
  COLETA_AGENDADA: { label: 'Coleta Agendada',variant: 'primary',   color: '#7367f0' },
  COLETADO:        { label: 'Coletado',       variant: 'primary',   color: '#7367f0' },
  RECEBIDO:        { label: 'Recebido',       variant: 'primary',   color: '#7367f0' },
  CONCLUIDO:       { label: 'Concluído',      variant: 'success',   color: '#28c76f' },
  CANCELADO:       { label: 'Cancelado',      variant: 'secondary', color: '#b8c2cc' },
};

const TRANSITIONS = {
  AGUARDANDO:      ['EM_ANALISE', 'CANCELADO'],
  EM_ANALISE:      ['APROVADO', 'REPROVADO', 'CANCELADO'],
  APROVADO:        ['COLETA_AGENDADA', 'CANCELADO'],
  COLETA_AGENDADA: ['COLETADO', 'CANCELADO'],
  COLETADO:        ['RECEBIDO'],
  RECEBIDO:        ['CONCLUIDO'],
  REPROVADO:       [],
  CONCLUIDO:       [],
  CANCELADO:       [],
};

export default {
  name: 'DetalhesDevolucao',
  data() {
    return {
      novoStatus: null,
      obsStatus: '',
      itemFields: [
        { key: 'descricao',  label: 'Produto' },
        { key: 'cor_tam',    label: 'Cor / Tam.' },
        { key: 'quantidade', label: 'Qtd.' },
        { key: 'motivo_item',label: 'Motivo' },
      ],
    };
  },
  computed: {
    ...mapGetters('Devolucoes', ['isLoading', 'isSaving', 'devolucaoAtual']),
    tipoVariant() { return this.devolucaoAtual?.tipo === 'TROCA' ? 'warning' : 'info'; },
    historicoCrescente() { return [...(this.devolucaoAtual?.historico || [])]; },
    canUpdateStatus() {
      const s = this.devolucaoAtual?.status;
      return s && TRANSITIONS[s] && TRANSITIONS[s].length > 0;
    },
    proximosStatus() {
      const s = this.devolucaoAtual?.status;
      return (TRANSITIONS[s] || []).map((v) => ({ value: v, label: STATUS_MAP[v]?.label || v }));
    },
  },
  created() {
    this.buscarPorId(this.$route.params.id);
  },
  methods: {
    ...mapActions('Devolucoes', ['buscarPorId', 'atualizarStatus']),
    async atualizarStatus() {
      if (!this.novoStatus) return;
      try {
        await this.$store.dispatch('Devolucoes/atualizarStatus', {
          id: this.$route.params.id,
          status: this.novoStatus,
          observacao: this.obsStatus,
        });
        this.novoStatus = null;
        this.obsStatus = '';
        this.$toast({ component: ToastificationContent, props: { title: 'Status atualizado!', icon: 'CheckIcon', variant: 'success' } });
      } catch (err) {
        this.$toast({ component: ToastificationContent, props: { title: 'Erro ao atualizar', text: err.message, icon: 'XIcon', variant: 'danger' } });
      }
    },
    statusLabel(s)   { return STATUS_MAP[s]?.label   || s; },
    statusVariant(s) { return STATUS_MAP[s]?.variant  || 'secondary'; },
    statusColor(s)   { return STATUS_MAP[s]?.color    || '#b8c2cc'; },
    formatDate(d)     { return d ? new Date(d).toLocaleDateString('pt-BR') : '—'; },
    formatDatetime(d) {
      if (!d) return '—';
      return new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
  },
};
</script>

<style scoped>
.timeline { position: relative; padding-left: 20px; }
.timeline-item { position: relative; padding: 0 0 18px 18px; }
.timeline-item::before {
  content: ''; position: absolute; left: 6px; top: 18px;
  bottom: 0; width: 2px; background: #ebe9f1;
}
.timeline-item--last::before { display: none; }
.timeline-dot {
  position: absolute; left: 0; top: 4px;
  width: 14px; height: 14px; border-radius: 50%;
  border: 2px solid #fff; box-shadow: 0 0 0 2px #ebe9f1;
}
.timeline-content { padding-top: 0; }
</style>
